import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system/legacy';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '@/services/supabase';
import { APP_CONSTANTS } from '@/config/constants';
import { fetchFile } from '@/utils/file/fileHelpers';
import { extractPathFromUrl } from '@/utils/file/uploadImageHelper';
import { DevChecklistEditorLogger } from '@/utils/logger';

type DevChecklistImageKind = 'foto' | 'assinatura' | 'marcacao';

const logger = DevChecklistEditorLogger.Image;

const resolveFolder = (kind: DevChecklistImageKind) => {
  const { CHECKLIST_PHOTOS, CHECKLIST_SIGNATURES, CHECKLIST_MARCACOES } = APP_CONSTANTS.STORAGE.FOLDER;
  if (kind === 'assinatura') return CHECKLIST_SIGNATURES;
  if (kind === 'marcacao') return CHECKLIST_MARCACOES;
  return CHECKLIST_PHOTOS;
};

const resolveExtensionFromDataUrl = (dataUrl: string, fallback: string) => {
  if (dataUrl.startsWith('data:image/png')) return 'png';
  if (dataUrl.startsWith('data:image/webp')) return 'webp';
  if (dataUrl.startsWith('data:image/gif')) return 'gif';
  if (dataUrl.startsWith('data:image/jpeg')) return 'jpg';
  return fallback;
};

const resolveContentType = (extension: string) => {
  switch (extension) {
    case 'png':
      return 'image/png';
    case 'webp':
      return 'image/webp';
    case 'gif':
      return 'image/gif';
    case 'jpg':
    case 'jpeg':
    default:
      return 'image/jpeg';
  }
};

const ensureMobileFileForDataUrl = async (dataUrl: string, extension: string) => {
  const base64 = dataUrl.split(',')[1] || '';
  const tempPath = `${FileSystem.cacheDirectory}dev-checklist-${uuidv4()}.${extension}`;
  await FileSystem.writeAsStringAsync(tempPath, base64, { encoding: FileSystem.EncodingType.Base64 });
  return tempPath;
};

export const uploadDevChecklistImage = async ({
  empresaId,
  checklistId,
  campoId,
  localUri,
  kind,
}: {
  empresaId: string;
  checklistId: string;
  campoId: string;
  localUri: string;
  kind: DevChecklistImageKind;
}): Promise<{ publicUrl: string; storagePath: string }> => {
  if (!empresaId || !checklistId || !campoId || !localUri) {
    throw new Error('Parâmetros obrigatórios ausentes para upload de imagem.');
  }

  const bucket = APP_CONSTANTS.STORAGE.BUCKET.CHECKLIST;
  const folder = resolveFolder(kind);
  const defaultExtension = kind === 'assinatura' ? 'png' : kind === 'marcacao' ? 'jpg' : 'jpg';
  const extension = localUri.startsWith('data:image')
    ? resolveExtensionFromDataUrl(localUri, defaultExtension)
    : (localUri.split('.').pop() || defaultExtension);
  const contentType = resolveContentType(extension);
  const filename = `${checklistId}-${campoId}-${Date.now()}-${uuidv4()}.${extension}`;
  const storagePath = `${folder}/${empresaId}/${filename}`;

  let resolvedUri = localUri;
  let shouldCleanup = false;
  if (Platform.OS !== 'web' && localUri.startsWith('data:image')) {
    resolvedUri = await ensureMobileFileForDataUrl(localUri, extension);
    shouldCleanup = true;
  }

  logger.info(`[uploadDevChecklistImage] Enviando ${kind} para ${storagePath}`);

  try {
    const payload = await fetchFile(resolvedUri);
    const { error } = await supabase.storage.from(bucket).upload(storagePath, payload, {
      contentType,
      upsert: true,
    });

    if (error) {
      throw error;
    }

    const { data } = supabase.storage.from(bucket).getPublicUrl(storagePath);
    if (!data?.publicUrl) {
      throw new Error('Falha ao resolver URL pública da imagem.');
    }

    return { publicUrl: data.publicUrl, storagePath };
  } finally {
    if (shouldCleanup) {
      try {
        await FileSystem.deleteAsync(resolvedUri, { idempotent: true });
      } catch {}
    }
  }
};

export const deleteDevChecklistImage = async (imageUrl?: string | null) => {
  if (!imageUrl) return;
  try {
    const { bucket, path } = extractPathFromUrl(imageUrl);
    const { error } = await supabase.storage.from(bucket).remove([path]);
    if (error) {
      throw error;
    }
    logger.info(`[deleteDevChecklistImage] Imagem removida: ${path}`);
  } catch (error) {
    logger.warn('[deleteDevChecklistImage] Falha ao remover imagem:', error);
  }
};

